// src/pages/CompanyPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
  clearError,
  exportCompanies,
  importPreview,
  importConfirm,
} from 'store/slices/companySlice';
import { Pencil, Trash2, MoreVertical, Loader2 } from 'lucide-react';
import DataTable from 'components/common/DataTable';
import PageHeader from 'components/common/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import { formatDateTime } from 'utlis/formateDateTime';
import { restoreRole } from 'store/slices/commonSlice';

const CompanyPage = () => {
  usePermissionSocket();
  const dispatch = useDispatch();

  const {
    companies,
    pagination,
    isLoading,
    error,
  } = useSelector((state) => state.company);

  const { canCreate, canUpdate, canDelete, canExport, canImport, canRead } = usePermissions('company');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [formData, setFormData] = useState({ name: '', is_active: true });
  const [formErrors, setFormErrors] = useState({});

  const refetch = useCallback(() => {
    dispatch(fetchCompanies({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'An error occurred');
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const openDialog = (company = null) => {
    if (company) {
      if (!canUpdate) return toast.error("No permission to edit");
      setCurrentCompany(company);
      setFormData({ name: company.name, is_active: company.is_active });
    } else {
      if (!canCreate) return toast.error("No permission to create");
      setCurrentCompany(null);
      setFormData({ name: '', is_active: true });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Company name is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (currentCompany) {
        await dispatch(updateCompany({ id: currentCompany.id, ...formData })).unwrap();
        toast.success('Company updated successfully');
      } else {
        await dispatch(createCompany(formData)).unwrap();
        toast.success('Company created successfully');
      }
      setDialogOpen(false);
      refetch();
    } catch {
      // Error handled globally
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return toast.error("No permission");
    try {
      await dispatch(deleteCompany(currentCompany.id)).unwrap();
      toast.success('Company deleted');
      setDeleteOpen(false);
      refetch();
    } catch { }
  };

  const handleImportPreview = async (file) => {
    if (!canImport) throw new Error("No import permission");
    return await dispatch(importPreview(file)).unwrap();
  };

  const handleImportConfirm = async (preview) => {
    if (!canImport) throw new Error("No import permission");
    await dispatch(importConfirm(preview)).unwrap();
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Company Name',
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'success' : 'secondary'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created On',
      cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created_at) ?? '-'}</span>,
    },
    {
      accessorKey: 'created_by_name',
      header: 'Created By',
      cell: ({ row }) => <span className="text-sm">{row.original.created_by_name ?? '-'}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const company = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canUpdate && (
                <DropdownMenuItem onClick={() => openDialog(company)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => {
                    setCurrentCompany(company);
                    setDeleteOpen(true);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Companies"
        description="Manage companies in the system"
        action={canCreate ? { label: 'Add Company', onClick: () => openDialog() } : null}
      />
      {/* 
      {isLoading && !companies.length ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading companies...
        </div>
      ) : null} */}

      {canRead ? (
        <DataTable
          data={companies}
          totalCount={pagination.count}
          columns={columns}
          templateUrl="/import-template/company.csv"
          templateDownloadName="company-import-template.csv"
          templateKey="company"
          searchPlaceholder="Search companies..."
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={Math.ceil(pagination.count / pagination.page_size)}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={isLoading}
          showExport={canExport}
          onExport={canExport ? () => dispatch(exportCompanies()) : undefined}
          showImport={canImport}
          onImportPreview={canImport ? handleImportPreview : undefined}
          onImportConfirm={canImport ? handleImportConfirm : undefined}
          onDelete={async (id) => {
            await dispatch(deleteCompany(id));
            refetch();
          }}
          onRestore={async (id) => {
            await dispatch(restoreRole({ entityType: 'company', id }));
            refetch();
          }}
        />
      ) : (
        <p className="text-center text-muted-foreground">You don't have permission to view companies.</p>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{currentCompany ? 'Edit Company' : 'Add Company'}</DialogTitle>
            <DialogDescription>
              {currentCompany ? 'Update company details' : 'Create a new company'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Company Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter company name"
                className={formErrors.name ? 'border-red-500' : ''}
              />
              {formErrors.name && <p className="text-sm text-red-600 mt-1">{formErrors.name}</p>}
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {currentCompany ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete <strong>{currentCompany?.name}</strong>.
              <br /><br />
              <strong>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanyPage;